import type { Document } from '@/types/generated'

/**
 * Composable for document thumbnail utilities
 *
 * Provides shared logic for extracting and displaying document thumbnails
 * from the imgproxy service URLs.
 */
export function useDocumentThumbnail() {
  /**
   * Get the thumbnail URL for a document
   *
   * Looks for the 'thumbnail' preset in the document's images array
   * which is generated by the backend's imgproxy integration.
   *
   * @param doc - The document object with potential images array
   * @returns The thumbnail URL or null if not available
   */
  const getThumbnailUrl = (doc: Document): string | null => {
    if (!doc?.images?.length) return null

    const thumbnail = doc.images.find((img: any) => img.image_preset === 'thumbnail')
    return thumbnail?.url || null
  }

  /**
   * Check if a document has a thumbnail available
   *
   * @param doc - The document to check
   * @returns true if thumbnail is available, false otherwise
   */
  const hasThumbnail = (doc: Document): boolean => {
    return getThumbnailUrl(doc) !== null
  }

  return {
    getThumbnailUrl,
    hasThumbnail
  }
}
